import { sanitizeNumber, fillObjectProps } from '@mixins';
import { IActor, Actor, ILocation } from '@types';
import { PortfolioModel } from './Actor/PortfolioModel';
import { PhotoModel } from './Actor/PhotoModel';
import { VideoModel } from './Actor/VideoModel';
import { VideoGalleryModel } from './Actor/VideoGalleryModel';

import { ActorsApi } from '@/api';
import { ActorPortfolioModel, ActorVideoRequestModel } from '@/model';

export class ActorModel implements IActor {
    [key: string]: any;
    id: number = null;
    cislo_hlasu: string = null;
    meno_priezvisko: string = null;
    pohlavie: string = null;
    jazyk: Actor.Language = null;
    mp3: string = null;
    mp3_biblia: string = null;
    chapter_biblia: number = 0;
    vysoky: boolean = false;
    hlboky: boolean = false;
    drsny: boolean = false;
    amatersky: boolean = false;
    detsky: boolean = false;
    full_exclusive: boolean = false;
    semi_exclusive: boolean = false;
    rights: boolean = false;
    absent: boolean = false;
    personalData: Actor.PersonalData = null;
    rating: Actor.Rating = null;
    photos: PhotoModel[] = [];
    videos: VideoModel[] = [];
    videoGallery: VideoGalleryModel[] = []; // TODO: remove
    videoRequests: ActorVideoRequestModel[] = [];
    portfolio: PortfolioModel[] = [];
    nominations: any = [];
    prices: any[] = [];
    character_id: number = null;
    locations: ILocation[] = null;

    constructor(data: IActor) {
        if (data.personalData) {
            data.personalData.rok_narodenia = sanitizeNumber(data.personalData.rok_narodenia);
        }

        fillObjectProps(this, data, {
            onFill: (key, value) => {
                switch (key) {
                    case 'photos':
                        if (!Array.isArray(value)) {
                            return false;
                        }
                        this[key] = value.map((item: Actor.Photo) => new PhotoModel(item));
                        return true;
                    case 'portfolio':
                        if (!Array.isArray(value)) {
                            return false;
                        }
                        this[key] = value.map((item: Actor.Portfolio) => new ActorPortfolioModel(item));
                        return true;
                    case 'videoRequests':
                        this[key] = this[key] = value.map(
                            (item: Actor.VideoRequest) => new ActorVideoRequestModel(item)
                        );
                        break;
                    // TODO: remove
                    case 'videoGallery':
                        if (!Array.isArray(value)) {
                            return false;
                        }
                        this[key] = value.map((item: Actor.VideoGallery) => new VideoGalleryModel(item));
                        return true;
                }
            },
        });
    }

    status: number;
    properties: any;

    async load(include: string | string[]) {
        include = Array.isArray(include) ? include : [include];
        const { data } = await ActorsApi.load(this.id, { include });
        const props = include.map((item) => item.split('.')[0]);
        props.forEach((prop) => {
            if (typeof data[prop] == 'undefined') return;
            switch (prop) {
                case 'photos':
                    this[prop] = data[prop].map((item: Actor.Photo) => new PhotoModel(item));
                    break;
                case 'portfolio':
                    this[prop] = data[prop].map((item: Actor.Portfolio) => new ActorPortfolioModel(item));
                    break;
                // Don't use this, not scoped
                // case 'videoRequests':
                //     this[prop] = data[prop].map((item: Actor.VideoRequest) => new ActorVideoRequestModel(item));
                //     break;
                default:
                    this[prop] = data[prop];
            }
        });
        return this;
    }

    getProfileData(): Actor.ProfileFormData {
        return {
            meno_priezvisko: this.meno_priezvisko,
            pohlavie: this.pohlavie,
            personalData: this.personalData,
            locations: this.locations,
            detail: {},
            properties: null,
            chapter_biblia: null,
            jazyk: null,
            cislo_hlasu: null,
            status: null,
        };
    }

    setProfileData(data: Actor.ProfileFormData) {
        this.meno_priezvisko = data.meno_priezvisko;
        this.pohlavie = data.pohlavie;
        this.personalData = {
            ...this.personalData,
            ...data.personalData,
            telefon: (data.personalData.telefon || '').replace(/\s+/g, ''),
            telefon2: (data.personalData.telefon2 || '').replace(/\s+/g, ''),
        };
        this.locations = data.locations;
    }

    // Photo --------------------------------------
    addPhoto(item: PhotoModel): ActorModel {
        this.photos.push(item);
        return this;
    }

    removePhoto(item: PhotoModel): ActorModel {
        this.photos = this.photos.filter((i) => i != item);
        return this;
    }

    setPhotos(photos: PhotoModel[]) {
        this.photos = photos;
        return this;
    }

    reorderPhotos() {
        this.photos = this.photos.map((item: PhotoModel, index: number) => {
            item.poradie = index + 1;
            return item;
        });
    }

    removePhotoByFileId(file_id: string): ActorModel {
        this.photos = this.photos.filter((image: PhotoModel) => image.file_id != file_id);
        return this;
    }

    // Video --------------------------------------
    addVideo(item: VideoGalleryModel): ActorModel {
        this.videoGallery.push(item);
        return this;
    }

    removeVideo(item: VideoGalleryModel): ActorModel {
        this.videoGallery = this.videoGallery.filter((i) => i != item);
        return this;
    }

    setVideos(items: VideoGalleryModel[]) {
        this.videoGallery = items;
        return this;
    }

    // Portfolio --------------------------------------
    addPortfolioItem(item: PortfolioModel): ActorModel {
        this.portfolio.push(item);
        this.portfolio.sort((a, b) => a.poradie - b.poradie);
        return this;
    }

    removePortfolioItem(item: PortfolioModel): ActorModel {
        this.portfolio = this.portfolio.filter((i) => i != item);
        return this;
    }

    loadContracts(): Promise<any> {
        return ActorsApi.loadContracts(this);
    }

    save(): Promise<any> {
        return ActorsApi.saveActor(this);
    }

    saveProfile(): Promise<any> {
        // TODO: merge after test
        // return Promise.all([ActorsApi.saveActorProfile(this), ActorsApi.saveActorLocations(this)]);
        return ActorsApi.saveActorProfile(this);
    }

    saveGallery(): Promise<any> {
        return ActorsApi.saveActorGallery(this);
    }

    saveVideoGallery(): Promise<any> {
        return ActorsApi.saveActorVideoGallery(this);
    }

    savePortfolio(): Promise<any> {
        return ActorsApi.saveActorPortfolio(this);
    }

    share(email: string, message: string) {
        return ActorsApi.shareActor(this, email, message);
    }
}
